import React, { useState } from "react";
import { Button, Form, Input } from "antd";
import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";

// import utils
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import isEmpty from "../../validations/is-empty";
import "../../style/auth/changePassword.scss";

const ChangePassword = ({ title }) => {
  document.title = title;

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    const { opassword, password, cpassword } = values;

    if (password !== cpassword) {
      openNotification(
        "error",
        "Validation Error!",
        "New password and confirm password do not match!",
      );
      return;
    }

    const params = {
      oldpassword: opassword,
      newpassword: password,
    };

    setLoading(true);

    apiClient
      .post("/users/change-password", params)
      .then((res) => {
        if (res.status === 200) {
          openNotification(
            "success",
            "Success",
            "Password changed successfully!",
          );
          form.resetFields();
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="change-password-page">
      <header className="change-password-heading">
        <span className="change-password-heading-icon">
          <LockOutlined />
        </span>
        <div>
          <h2 className="title">Change Password</h2>
          <p>Use a strong, unique password to protect your account.</p>
        </div>
      </header>
      <div className="change-password-layout">
        <aside className="password-security-note">
          <span className="password-security-icon">
            <SafetyCertificateOutlined />
          </span>
          <h3>Keep your account secure</h3>
          <p>
            Your new password should be at least 6 characters and different from
            your current password.
          </p>
          <ul>
            <li>Use a mix of letters and numbers</li>
            <li>Avoid names or common phrases</li>
            <li>Never reuse a shared password</li>
          </ul>
        </aside>
        <Form
          form={form}
          className="change-password-form"
          name="changePassword"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <div className="change-password-form-heading">
            <strong>Update credentials</strong>
            <span>Enter your current password before choosing a new one.</span>
          </div>
          <Form.Item
            label="Old Password"
            name="opassword"
            rules={[
              { required: true, message: "Please input your old password!" },
            ]}
          >
            <Input.Password size="large" placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="password"
            rules={[
              { required: true, message: "Please input your new password!" },
              {
                min: 6,
                message: "Password must be at least 6 characters long!",
              },
            ]}
          >
            <Input.Password size="large" placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="cpassword"
            dependencies={["password"]}
            hasFeedback
            rules={[
              { required: true, message: "Please confirm your password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match!"),
                  );
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Repeat new password" />
          </Form.Item>

          <Form.Item className="change-password-submit">
            <Button type="primary" htmlType="submit" loading={loading}>
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default ChangePassword;
